import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { setActiveHouseholdId } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { HouseholdResponse } from '@/lib/types';

const SELECTED_HOUSEHOLD_KEY = 'cp.active_household_id';

type HouseholdContextValue = {
  households: HouseholdResponse[];
  currentHouseholdId: number | null;
  currentHousehold: HouseholdResponse | null;
  householdCount: number;
  setCurrentHouseholdId: (id: number, opts?: { track?: boolean }) => void;
};

const HouseholdContext = createContext<HouseholdContextValue | null>(null);

export function useHouseholds(): HouseholdContextValue {
  const ctx = useContext(HouseholdContext);
  if (!ctx) {
    throw new Error('useHouseholds must be used within a HouseholdProvider');
  }
  return ctx;
}

export function useOptionalHouseholds(): HouseholdContextValue | null {
  return useContext(HouseholdContext);
}

/**
 * Builds a switcher label: joined co-parent + children names, with a stable
 * "Household N" numeral (oldest-first) fallback.
 */
export function householdLabel(
  household: HouseholdResponse,
  index: number,
  currentUserId: number | null | undefined
): string {
  const numeral = `Household ${index + 1}`;
  const coparent = household.members.find((m) => m.user_id !== currentUserId);
  const coparentName =
    coparent?.first_name?.trim() || coparent?.last_name?.trim();
  const childNames = household.beneficiaries
    .filter((b) => b.type === 'CHILD')
    .map((b) => b.name?.trim())
    .filter((n): n is string => Boolean(n));

  const descriptors: string[] = [];
  if (coparentName) descriptors.push(`with ${coparentName}`);
  if (childNames.length > 0) descriptors.push(childNames.join(', '));

  if (descriptors.length === 0) return numeral;
  return `${numeral} · ${descriptors.join(' · ')}`;
}

function readPersisted(): number | null {
  try {
    const raw = window.localStorage.getItem(SELECTED_HOUSEHOLD_KEY);
    const parsed = raw != null && raw !== '' ? Number(raw) : null;
    return parsed != null && Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function HouseholdProvider({
  households,
  children,
}: {
  households: HouseholdResponse[];
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [currentHouseholdId, setIdState] = useState<number | null>(() =>
    readPersisted()
  );

  const sortedIds = useMemo(
    () => households.map((h) => h.id).sort((a, b) => a - b),
    [households]
  );
  const sortedIdsKey = sortedIds.join(',');

  // Resolve the active household against the live list (oldest-first default,
  // and graceful fallback when the persisted/selected id is stale).
  useEffect(() => {
    if (sortedIds.length === 0) {
      setActiveHouseholdId(null);
      return;
    }
    setIdState((prev) => {
      let next = prev;
      if (next == null || !sortedIds.includes(next)) {
        const persisted = readPersisted();
        next =
          persisted != null && sortedIds.includes(persisted)
            ? persisted
            : sortedIds[0];
      }
      setActiveHouseholdId(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedIdsKey]);

  const setCurrentHouseholdId = useCallback(
    (id: number, opts?: { track?: boolean }) => {
      setIdState(id);
      setActiveHouseholdId(id);
      try {
        window.localStorage.setItem(SELECTED_HOUSEHOLD_KEY, String(id));
      } catch {
        // ignore storage errors
      }
      void queryClient.invalidateQueries();
      if (opts?.track !== false) {
        track('Household Switched', { active_household_id: id });
      }
    },
    [queryClient]
  );

  const currentHousehold = useMemo(
    () => households.find((h) => h.id === currentHouseholdId) ?? null,
    [households, currentHouseholdId]
  );

  const value = useMemo<HouseholdContextValue>(
    () => ({
      households,
      currentHouseholdId,
      currentHousehold,
      householdCount: households.length,
      setCurrentHouseholdId,
    }),
    [households, currentHouseholdId, currentHousehold, setCurrentHouseholdId]
  );

  return (
    <HouseholdContext.Provider value={value}>
      {children}
    </HouseholdContext.Provider>
  );
}
